import { useConnector } from "@solana/connector/react";
import { PublicKey } from "@solomon-labs/publickey";
import type { SolanaAddress } from "@solomon-labs/types";
import { useEffect, useMemo, useRef, useState } from "react";

import { useSolomonE2EWallet } from "../e2e";
import type { WalletState } from "../types";

/** Delay before considering wallet ready for API calls */
const WALLET_STABILIZATION_DELAY = 500;

/**
 * Hook to access wallet state with solomon-labs types
 *
 * Wraps ConnectorKit's useConnector hook to provide:
 * - Branded PublicKey type from @solomon-labs/publickey
 * - Simplified API for common wallet operations
 *
 * @example
 * ```tsx
 * const { connected, publicKey, disconnect } = useWallet();
 *
 * if (connected && publicKey) {
 *   console.log(`Connected: ${publicKey.toString()}`);
 * }
 * ```
 */
export function useWallet(): WalletState {
    const e2eWallet = useSolomonE2EWallet();
    const connector = useConnector();

    if (e2eWallet) {
        return e2eWallet;
    }

    // Note: connector.account is the address string directly, not an object with .address
    const accountAddress = typeof connector.account === "string" ? connector.account : null;

    // Convert ConnectorKit address to solomon-labs PublicKey
    const publicKey = useMemo((): PublicKey | null => {
        if (!accountAddress) {
            return null;
        }
        try {
            // ConnectorKit returns address as base58 string directly
            // Cast through string since both Address and SolanaAddress are branded string types
            return new PublicKey(accountAddress as unknown as SolanaAddress);
        } catch (error) {
            console.error("Failed to parse wallet address:", error);
            return null;
        }
    }, [accountAddress]);

    // Stabilization state - becomes true 500ms after wallet is fully connected
    const [isWalletReady, setIsWalletReady] = useState(false);
    const stabilizationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Manage wallet readiness with stabilization delay
    useEffect(() => {
        // Clear any pending timer on state change
        if (stabilizationTimerRef.current) {
            clearTimeout(stabilizationTimerRef.current);
            stabilizationTimerRef.current = null;
        }

        const isStable = connector.isConnected && !connector.isConnecting && publicKey !== null;

        if (isStable) {
            // Start stabilization timer
            stabilizationTimerRef.current = setTimeout(() => {
                setIsWalletReady(true);
            }, WALLET_STABILIZATION_DELAY);
        } else {
            // Immediately mark as not ready
            setIsWalletReady(false);
        }

        // Cleanup on unmount or state change
        return (): void => {
            if (stabilizationTimerRef.current) {
                clearTimeout(stabilizationTimerRef.current);
                stabilizationTimerRef.current = null;
            }
        };
    }, [connector.isConnected, connector.isConnecting, publicKey]);

    return {
        connected: connector.isConnected,
        connecting: connector.isConnecting,
        isWalletReady,
        publicKey,
        walletName: connector.connector?.name ?? null,
        walletIcon: connector.connector?.icon ?? null,
        disconnect: connector.disconnectWallet
    };
}
